import * as React from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  createInfoWindow as createWaypointInfoWindow,
  getCoordinatesFromPaths,
} from './WaypointService';
import { IInfoChipsOverlays } from './DrawedWaypoint';
import { IDetailWaypointResponseData, IWaypointPostion } from './types';
import {
  centerWaypointAtom,
  computedAreaAtom,
  coordinatesAtom,
  isEditModeAtom,
  isWaypointsOpenAtom,
  waypointDataAtom,
  waypointIdAtom,
} from './WaypointState';
import { autofocusAtom } from '../OnlineMapState';

export interface IDrawedWaypointProps {
  map: google.maps.Map;
}

interface IWaypointPolygon {
  [waypointId: number]: google.maps.Polygon;
}

function EditedDrawedWaypoint(props: IDrawedWaypointProps): null {
  const [isWaypointsOpen] = useRecoilState(isWaypointsOpenAtom);
  const { current: infoAreaOverlay } = React.useRef<IInfoChipsOverlays>({});
  const [computedArea, setComputedArea] = useRecoilState<number>(computedAreaAtom);
  const [coordinates, setCoordinates] = useRecoilState<IWaypointPostion[] | null>(coordinatesAtom);
  const [isEditMode, setIsEditMode] = useRecoilState<boolean>(isEditModeAtom);
  const [waypointId, setWaypointId] = useRecoilState<number | null>(waypointIdAtom);
  const [waypointData, setWaypointData] = useRecoilState<IDetailWaypointResponseData | null>(
    waypointDataAtom
  );
  const [drawedPolygon, setDrawedPolygon] = React.useState<google.maps.Polygon | null>(null);
  const setCenter = useSetRecoilState<IWaypointPostion | null>(centerWaypointAtom);
  const setCenterWaypoint = useSetRecoilState<IWaypointPostion | null>(centerWaypointAtom);
  const autofocus = useRecoilValue<boolean>(autofocusAtom);

  const createInfoWindow = React.useCallback(
    (
      position: google.maps.LatLng,
      waypointID: number | string,
      createdPolygon: google.maps.Polygon
    ) => {
      const areaInfo = createWaypointInfoWindow(position, createdPolygon);
      infoAreaOverlay[waypointID] = areaInfo.newWindow;
      setComputedArea(areaInfo.area);
      infoAreaOverlay[waypointID].setMap(props.map);
    },
    [infoAreaOverlay, props.map, setComputedArea]
  );

  React.useEffect(() => {
    if (isEditMode) {
      if (props.map && waypointId) {
        if (waypointData && waypointData.coordinates) {
          const polygonPath: any[] = [];
          waypointData.coordinates.forEach((waypointPosition) => {
            polygonPath.push({
              lat: waypointPosition.latitude,
              lng: waypointPosition.longitude,
            });
          });

          if (!drawedPolygon) {
            const newPolygon = new google.maps.Polygon({
              paths: polygonPath,
              strokeColor: '#B50536',
              strokeWeight: 3,
              fillColor: '#FCD4DF',
              fillOpacity: 0.5,
              editable: true,
              draggable: true,
            });
            setDrawedPolygon(newPolygon);
            // newPolygon.setMap(props.map);

            // if (setPolygon) {
            //   setPolygon(newPolygon);
            // }

            // waypointPolygons[waypointId] = newPolygon;

            // waypointPolygons[waypointId].setMap(props.map);

            const bounds = new google.maps.LatLngBounds();
            polygonPath.forEach((path) => {
              bounds.extend(path);
            });
            const centerPolygon = new google.maps.LatLng(
              bounds.getCenter().lat(),
              bounds.getCenter().lng()
            );

            createInfoWindow(centerPolygon, 'drawed-waypoint', newPolygon);

            props.map.fitBounds(bounds, 100);
          }
        }
      }
    }

    if (!isWaypointsOpen) {
      setWaypointData(null);
      setWaypointId(null);
      setIsEditMode(false);
      if (drawedPolygon) {
        drawedPolygon.setMap(null);
        setDrawedPolygon(null);
      }
    }
    if (!isEditMode) {
      if (drawedPolygon) drawedPolygon.setMap(null);
      setDrawedPolygon(null);
      const removeChips = infoAreaOverlay;

      Object.keys(removeChips).forEach((remove) => {
        infoAreaOverlay[remove].setMap(null);
        delete infoAreaOverlay[remove];
      });
    }

    return () => {
      if (drawedPolygon && !isEditMode) {
        drawedPolygon.setMap(null);
        setDrawedPolygon(null);

        const removeChips = infoAreaOverlay;

        Object.keys(removeChips).forEach((remove) => {
          infoAreaOverlay[remove].setMap(null);
          delete infoAreaOverlay[remove];
        });
      }
      if (!isEditMode) {
        setDrawedPolygon(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, isWaypointsOpen, props.map, waypointData, waypointId]);

  React.useEffect(() => {
    if (drawedPolygon) {
      drawedPolygon.setMap(props.map);
      const bounds = new google.maps.LatLngBounds();
      drawedPolygon
        .getPath()
        .getArray()
        .forEach((path) => {
          bounds.extend(path);
        });

      const center: IWaypointPostion = {
        latitude: bounds.getCenter().lat(),
        longitude: bounds.getCenter().lng(),
      };

      setCenter(center);

      props.map.fitBounds(bounds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawedPolygon]);

  React.useEffect(() => {
    if (drawedPolygon) {
      drawedPolygon.setMap(props.map);
      const removeChips = infoAreaOverlay;

      Object.keys(removeChips).forEach((remove) => {
        infoAreaOverlay[remove].setMap(null);
        delete infoAreaOverlay[remove];
      });
      const bounds = new google.maps.LatLngBounds();
      drawedPolygon
        .getPath()
        .getArray()
        .forEach((path) => {
          bounds.extend(path);
        });

      const centerPolygon = new google.maps.LatLng(
        bounds.getCenter().lat(),
        bounds.getCenter().lng()
      );
      const center: IWaypointPostion = {
        latitude: bounds.getCenter().lat(),
        longitude: bounds.getCenter().lng(),
      };

      if (autofocus) {
        props.map.fitBounds(bounds, { top: 50, right: 50, bottom: 100, left: 200 });
      }

      setCenterWaypoint(center);

      createInfoWindow(centerPolygon, 'drawed-waypoint', drawedPolygon);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates, autofocus]);

  React.useEffect(() => {
    if ((drawedPolygon && isWaypointsOpen && isEditMode) || (drawedPolygon && isWaypointsOpen)) {
      google.maps.event.addListenerOnce(
        drawedPolygon,
        'mouseup',
        (_event: google.maps.PolyMouseEvent) => {
          const coords = drawedPolygon.getPath().getArray();
          // setRawCoordinates(coords);
          setCoordinates(getCoordinatesFromPaths(coords));
        }
      );
    }
  });

  // React.useEffect(() => {
  //   if (props.map && waypointId && isEditMode && isWaypointsOpen) {
  //     google.maps.event.addListenerOnce(
  //       waypointPolygons[waypointId],
  //       'rightclick',
  //       (e: google.maps.PolyMouseEvent) => {
  //         // Check if click was on a vertex control point
  //         if (e.vertex === undefined || e.vertex <= 2) {
  //           if (e.vertex) {
  //             setIsVertexAlertShown(true);
  //             console.info('Cannot delete vertex: ', e.vertex);
  //           }
  //           return;
  //         }
  //         waypointPolygons[waypointId].getPath().removeAt(e.vertex);
  //         const coordinates = waypointPolygons[waypointId].getPath().getArray();
  //         if (setCoordinates) setCoordinates(waypoint.getCoordinatesFromPaths(coordinates));

  //         const bounds = new google.maps.LatLngBounds();
  //         coordinates.map((path) => {
  //           bounds.extend(path);
  //         });

  //         const center: IWaypointPostion = {
  //           latitude: bounds.getCenter().lat(),
  //           longitude: bounds.getCenter().lng(),
  //         };
  //         if (setCenter) setCenter(center);
  //       }
  //     );
  //   }
  // });

  // const handleCloseVertexAlert = () => {
  //   setIsVertexAlertShown(false);
  // };
  // if (isVertexAlertShown) {
  //   return (
  //     <Alert
  //       data={{
  //         title: 'waypoints.alert.cannotDeleteVertex.title',
  //         message: 'waypoints.alert.cannotDeleteVertex.message',
  //         type: 'warning',
  //       }}
  //       isAbsolute={true}
  //       onCloseCallback={handleCloseVertexAlert}
  //       id="vertex-delete-alert"
  //       timeout={3000}
  //     />
  //   );
  // }
  return null;
}

export default EditedDrawedWaypoint;
